import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { useFeedDirectory, useNowPlaying } from "../../api/hooks.js";
import {
  FeedStatePill,
  feedStateCopy,
} from "../../design-system/feed-state.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { formatDeliveryFormat } from "../../lib/format.js";
import { useNow } from "../../lib/use-now.js";
import { Artwork, LiveBars } from "../../player/artwork.js";
import { BlockedPanel } from "../../player/blocked-notice.js";
import { useFeedPlayback, usePlayer } from "../../player/player-provider.js";
import { AiredProgress, PlayButton } from "../../player/transport.js";
import { RecentlyPlayed } from "./recently-played.js";

/**
 * One feed, in full: what is on it, what it publishes, and what it has played. This
 * is the desktop counterpart of the mobile sheet — the same facts, laid out for a
 * surface that has room for them rather than one that is held in a hand.
 */
export function FeedPage() {
  const { feedId = "" } = useParams();
  const directory = useFeedDirectory();
  const live = useNowPlaying(feedId);
  const { detail, play, stop } = usePlayer();
  const { isCurrent, state } = useFeedPlayback(feedId);
  const now = useNow(1000);

  const airing = live.data?.nowPlaying ?? null;
  const named = directory.data?.feeds.find((feed) => feed.id === feedId);
  const feedName = named?.name ?? "Feed";
  const streamUrl = live.data?.streamUrl ?? null;
  const delivery = live.data?.deliveries[0];

  if (live.error && !live.data) {
    return (
      <div className="flex flex-col gap-6">
        <BackLink />
        <StateSurface kind="error" title="This feed could not be read">
          {live.error}
        </StateSurface>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <BackLink />

      <div className="grid gap-6 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] sm:gap-8 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        <Artwork
          airing={airing}
          className="w-full max-w-[15rem] shadow-[0_20px_50px_var(--accent-glow)] sm:max-w-none"
        />

        <div className="flex min-w-0 flex-col gap-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[0.625rem] tracking-[0.14em] text-ink-3 uppercase">
                {feedName}
              </span>
              {isCurrent && state === "playing" ? (
                <LiveBars className="h-2.5 text-accent-text" />
              ) : null}
            </div>
            <h1 className="mt-2 text-balance text-2xl leading-tight font-semibold text-ink lg:text-3xl">
              {live.loading ? "…" : (airing?.title ?? "Nothing airing")}
            </h1>
            {airing?.artist ? (
              <p className="mt-1.5 text-base text-ink-2">{airing.artist}</p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <FeedStatePill state={live.data?.state ?? null} />
            <span className="text-xs text-ink-3">
              {feedStateCopy(live.data?.state ?? null).detail}
            </span>
          </div>

          <AiredProgress
            className="max-w-md"
            now={now}
            snapshot={live.data}
            withTimes
          />

          <div className="flex items-center gap-4">
            <PlayButton
              disabled={!streamUrl}
              feedName={feedName}
              onActivate={() => {
                if (!streamUrl) return;
                if (
                  isCurrent &&
                  state !== "idle" &&
                  state !== "error" &&
                  state !== "blocked"
                ) {
                  stop();
                } else play({ feedId, feedName, streamUrl });
              }}
              size="large"
              state={state}
            />
            <div className="min-w-0 text-sm text-ink-3">
              {isCurrent && state === "playing"
                ? "Listening now."
                : streamUrl
                  ? "Listen to this feed."
                  : "Nothing to listen to yet."}
            </div>
          </div>

          {isCurrent && state === "blocked" ? (
            <BlockedPanel streamUrl={detail} />
          ) : null}
          {isCurrent && state === "error" && detail ? (
            <p className="text-sm text-err">{detail}</p>
          ) : null}

          {live.data && live.data.deliveries.length > 0 ? (
            <dl className="grid gap-x-8 gap-y-3 border-t border-border pt-5 text-sm sm:grid-cols-2">
              <Fact label="Delivery">
                {delivery ? formatDeliveryFormat(delivery) : "—"}
              </Fact>
              <Fact label="Address">
                <span className="font-mono text-xs break-all text-ink-2">
                  {live.data.streamUrl ?? "—"}
                </span>
              </Fact>
              <Fact label="Declared loudness">
                {`${String(live.data.loudness.integratedLufs)} LUFS · ${String(live.data.loudness.truePeakDbtp)} dBTP`}
              </Fact>
              {live.data.deliveries.length > 1 ? (
                <Fact label="Also published">
                  {`${String(live.data.deliveries.length - 1)} more delivery`}
                </Fact>
              ) : null}
            </dl>
          ) : null}
        </div>
      </div>

      <RecentlyPlayed feedId={feedId} heading="Recently played" />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      className="inline-flex w-fit items-center gap-2 text-sm text-ink-3 transition-colors hover:text-ink"
      to="/feeds"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Feeds
    </Link>
  );
}

function Fact({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-1 min-w-0 text-ink-2">{children}</dd>
    </div>
  );
}
